import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  PatchUsersRequest,
  PatchUsersResponse,
  PatchUsersV2TransformedResponse,
  PermissionLevel,
  UserInfo,
} from '../api/resources/userManagement/types';
import useRequiredParams from '../hooks/useRequiredParams';
import { Role } from '../types/resources/userManagement';

type UserManagementApi = {
  getUsers: (accountId: string) => Promise<UserInfo[]>;
  patchUsers: (
    body: Omit<PatchUsersRequest, 'account_id'>,
  ) => Promise<PatchUsersResponse>;
  deleteUsers: (usersEmail: string[]) => Promise<any>;
  /** V2 enriched GET — returns UserInfo[] with active + invite_expires_at */
  getEnrichedUsers?: (accountId: string) => Promise<UserInfo[]>;
  /** V2 PATCH — invite flow with per-user status reporting */
  patchUsersV2?: (
    body: Omit<PatchUsersRequest, 'account_id'>,
  ) => Promise<PatchUsersV2TransformedResponse>;
  /** V2 POST — resend invitation for pending users */
  resendInvite?: (userEmail: string) => Promise<any>;
};

type UseUserManagementProps = {
  api: UserManagementApi;
  /** When true, uses v2 endpoints (enriched GET, invite PATCH) + separate query key */
  useV2Endpoints?: boolean;
  isEnabled: boolean;
  currentUserEmail?: string;
};

const emptyUsersForAccount: UserInfo[] = [];

const useUserManagement = ({
  api,
  useV2Endpoints = false,
  isEnabled,
  currentUserEmail,
}: UseUserManagementProps) => {
  const { accountId } = useRequiredParams<{ accountId: string }>();
  const queryClient = useQueryClient();

  // Use a separate query key when `enriched` is true so the v2 data
  // (with `active`/`invite_expires_at`) doesn't collide with v1 callers
  // (e.g. `useCurrentUserTags`) that share the plain key.
  const queryKey = useV2Endpoints
    ? ['users', accountId, 'v2']
    : ['users', accountId];

  const fetchUsers =
    useV2Endpoints && api.getEnrichedUsers
      ? api.getEnrichedUsers
      : api.getUsers;
  const mutateUsers =
    useV2Endpoints && api.patchUsersV2 ? api.patchUsersV2 : api.patchUsers;

  // TODO: passing in account ID because if we do not go via the landing page then the account Id isn't set properly
  // Might get fixed as part of Next js migration so double check this
  const { data: usersForAccount, isFetching: isLoadingUsersInfo } = useQuery<
    UserInfo[]
  >(queryKey, () => fetchUsers(accountId), {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!accountId && isEnabled,
    onSuccess: () => {
      resetUsersMutationState();
    },
  });

  const {
    mutate: createUsers,
    isLoading: isCreatingUsers,
    isSuccess: isCreateSuccess,
    reset: resetCreateUsersMutationState,
    data: createUsersData,
  } = useMutation<
    PatchUsersResponse | PatchUsersV2TransformedResponse,
    Error,
    PatchUsersRequest
  >(args => mutateUsers(args), {
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });

  const {
    mutate: patchUsers,
    isLoading: isPatchingUsers,
    isSuccess: isPatchSuccess,
    reset: resetPatchUsersMutationState,
    data: patchUsersData,
  } = useMutation<
    PatchUsersResponse | PatchUsersV2TransformedResponse,
    Error,
    PatchUsersRequest
  >(args => mutateUsers(args), {
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });

  const {
    mutate: deleteUsers,
    isLoading: isDeletingUsers,
    isSuccess: isDeleteSuccess,
    reset: resetDeleteUsersMutationState,
    variables: usersToDelete,
  } = useMutation<{ success: boolean }, Error, string[]>(
    usersEmail => api.deleteUsers(usersEmail),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    },
  );

  const {
    mutate: resendInvite,
    isLoading: isResendingInvite,
    isSuccess: isResendInviteSuccess,
    isError: isResendInviteError,
    reset: resetResendInviteMutationState,
  } = useMutation<any, Error, string>(
    userEmail =>
      api.resendInvite?.(userEmail) ??
      Promise.reject(new Error('resendInvite not available')),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    },
  );

  const currentUserRole = useMemo(() => {
    // all user role emails are stored in lowercase
    const sanitisedUserEmail = currentUserEmail?.toLowerCase();
    const currUserDetails = usersForAccount?.find(
      user => user.user_email === sanitisedUserEmail,
    );

    return currUserDetails?.role;
  }, [usersForAccount, currentUserEmail]);

  // Used to reset the mutation state when cycling through the user table for different accounts
  // so that any notifications from previous mutations are removed
  const resetUsersMutationState = () => {
    resetCreateUsersMutationState();
    resetDeleteUsersMutationState();
    resetPatchUsersMutationState();
    resetResendInviteMutationState();
  };

  // hack to get agentic kitchen group permissions
  // in long run we should revisit the permission model to be more granular.
  const isAgenticKitchenUser = useMemo(() => {
    // all user role emails are stored in lowercase
    const sanitisedUserEmail = currentUserEmail?.toLowerCase();
    const currUserDetails = usersForAccount?.find(
      user => user.user_email === sanitisedUserEmail,
    );
    return currUserDetails?.tags.some(t => t === 'agentic-kitchen');
  }, [usersForAccount, currentUserEmail]);

  const userPermissionsPerProject = useMemo(() => {
    if (!usersForAccount) {
      return {};
    }

    const permissions: Record<
      string,
      Record<string, Record<string, PermissionLevel>>
    > = {};

    usersForAccount.forEach(user => {
      const userEmail = user.user_email.toLowerCase();
      permissions[userEmail] = {};

      Object.entries(user.per_project_config || {}).forEach(
        // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
        ([projectId, config]) => {
          permissions[userEmail][projectId] = {};

          // Check if V2 fields exist and have values
          const hasReadFeatures =
            config.read_features && config.read_features.length > 0;
          const hasWriteFeatures =
            config.write_features && config.write_features.length > 0;

          if (hasReadFeatures || hasWriteFeatures) {
            // V2: Use read_features and write_features arrays
            const writeFeaturesSet = new Set(config.write_features || []);
            const readFeaturesSet = new Set(config.read_features || []);

            // Collect all features mentioned in either array
            const allFeatures = new Set([
              ...(config.write_features || []),
              ...(config.read_features || []),
            ]);

            allFeatures.forEach(feature => {
              if (writeFeaturesSet.has(feature)) {
                permissions[userEmail][projectId][feature] = 'edit';
              } else if (readFeaturesSet.has(feature)) {
                permissions[userEmail][projectId][feature] = 'read';
              } else {
                permissions[userEmail][projectId][feature] = 'none';
              }
            });
          } else {
            // V1: Fallback to role and feature_enabled
            const role = config.role;
            const enabledFeatures = config.feature_enabled || [];

            // Map role to permission level
            let rolePermission: 'read' | 'edit' = 'read';
            if (role === Role.EDITOR) {
              rolePermission = 'edit';
            } else if (role === Role.VIEWER) {
              rolePermission = 'read';
            }

            // For each feature in feature_enabled, assign the role permission
            enabledFeatures.forEach(feature => {
              permissions[userEmail][projectId][feature] = rolePermission;
            });
          }
        },
      );
    });

    return permissions;
  }, [usersForAccount]);

  return {
    usersForAccount: usersForAccount || emptyUsersForAccount,
    currentUserRole,
    isLoadingUsersInfo,
    createUsers,
    createUsersData,
    isCreatingUsers,
    isCreateSuccess,
    patchUsers,
    patchUsersData,
    isPatchingUsers,
    isPatchSuccess,
    deleteUsers,
    usersToDelete,
    isDeletingUsers,
    isDeleteSuccess,
    isAgenticKitchenUser,
    userPermissionsPerProject,
    resendInvite,
    isResendingInvite,
    isResendInviteSuccess,
    isResendInviteError,
  };
};

export default useUserManagement;
